<!-- ============================================================
 * ContactFileUpload.vue
 * 첨부파일 업로드 컴포넌트 (드래그앤드롭 · 썸네일 미리보기 · 개별 삭제)
 * 정책: JPG·PNG·GIF·WebP·PDF / 파일당 5MB / 최대 5개 / 총 10MB (§6.3)
 * 사용: v-model="files" (File[] 배열)
 * 공개 메서드: reset() — 드로어 닫기 등 외부 초기화 시 호출
 * ============================================================ -->
<template>
    <div class="cfu">
        <!-- 드롭존 -->
        <label
            class="cfu__zone"
            :class="{ 'cfu__zone--over': isDragover, 'cfu__zone--err': !!errorMsg }"
            @dragenter.prevent="isDragover = true"
            @dragover.prevent="isDragover = true"
            @dragleave.prevent="isDragover = false"
            @drop.prevent="onDrop"
        >
            <input
                ref="fileInput"
                type="file"
                :accept="ACCEPT"
                multiple
                class="cfu__input"
                @change="onFileChange"
            />

            <!-- 업로드 아이콘 -->
            <span class="cfu__icon" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="17 8 12 3 7 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
            </span>

            <!-- 파일 선택 버튼 레이블 -->
            <span class="cfu__btn-label">파일 선택</span>

            <!-- 안내 텍스트 -->
            <span class="cfu__guide">
                JPG · PNG · GIF · WebP · PDF&ensp;/&ensp;파일당 5MB · 최대 5개 · 총 10MB<br>
                오류 화면이나 문의 관련 자료를 첨부해 주세요. 끌어다 놓아도 됩니다.
            </span>
        </label>

        <!-- 오류 메시지 -->
        <p v-if="errorMsg" class="cfu__errmsg" role="alert">{{ errorMsg }}</p>

        <!-- 파일 미리보기 목록 -->
        <ul v-if="files.length" class="cfu__list" role="list">
            <li
                v-for="(f, i) in files"
                :key="f.name + '_' + f.size"
                class="cfu__item"
            >
                <!-- 썸네일 -->
                <span class="cfu__thumb" :class="{ 'cfu__thumb--pdf': isPDF(f) }">
                    <img
                        v-if="isImg(f) && thumbs[i]"
                        :src="thumbs[i]"
                        :alt="f.name"
                        class="cfu__thumb-img"
                    />
                    <span v-else class="cfu__thumb-ext">{{ ext(f) }}</span>
                </span>

                <!-- 파일 정보 -->
                <span class="cfu__meta">
                    <span class="cfu__name" :title="f.name">{{ f.name }}</span>
                    <span class="cfu__size">{{ fmtSize(f.size) }}</span>
                </span>

                <!-- 삭제 버튼 -->
                <button
                    type="button"
                    class="cfu__del"
                    :aria-label="f.name + ' 첨부 삭제'"
                    @click.stop="remove(i)"
                >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                </button>
            </li>
        </ul>
    </div>
</template>

<script>
/** 허용 MIME 타입 */
const ACCEPT   = 'image/jpeg,image/png,image/gif,image/webp,application/pdf';
const ALLOWED  = new Set(['image/jpeg','image/png','image/gif','image/webp','application/pdf']);
// 백엔드(InquiryUsageService)는 확장자로 검증하므로 프론트도 확장자를 1차 기준으로 둔다.
// (일부 환경에서 정상 파일의 MIME(f.type)이 비어 들어와 부당 거부되는 것을 방지)
const ALLOWED_EXT = ['jpg','jpeg','png','gif','webp','pdf'];
const MAX_FILE  = 5  * 1024 * 1024; // 5MB
const MAX_TOTAL = 10 * 1024 * 1024; // 10MB
const MAX_COUNT = 5;

export default {
    name: 'ContactFileUpload',

    model: { prop: 'value', event: 'input' },

    props: {
        /** v-model 바인딩 — File 배열 */
        value: { type: Array, default: () => [] },
    },

    data() {
        return {
            ACCEPT,
            files:     [],    // 내부 File 배열
            thumbs:    [],    // ObjectURL 캐시 (index 대응)
            isDragover: false,
            errorMsg:  '',
        };
    },

    watch: {
        // 외부에서 빈 배열로 초기화 시 내부도 동기화
        value(v) {
            if (!v.length && this.files.length) this._reset();
        },
    },

    beforeDestroy() {
        // 생성한 ObjectURL 메모리 해제
        this.thumbs.forEach(u => u && URL.revokeObjectURL(u));
    },

    methods: {
        // ── 이벤트 핸들러 ────────────────────────────────
        onFileChange(e) {
            this._addFiles(e.target.files);
            e.target.value = ''; // 동일 파일 재선택 허용
        },

        onDrop(e) {
            this.isDragover = false;
            if (e.dataTransfer?.files?.length) this._addFiles(e.dataTransfer.files);
        },

        // ── 파일 추가 (검증 포함) ─────────────────────────
        _addFiles(incoming) {
            const next   = [...this.files];
            const nextTh = [...this.thumbs];
            let err = '';

            for (const f of Array.from(incoming)) {
                if (next.length >= MAX_COUNT) {
                    err = `첨부파일은 최대 ${MAX_COUNT}개까지 올릴 수 있어요.`;
                    break;
                }
                const fileExt = (f.name.split('.').pop() ?? '').toLowerCase();
                if (!ALLOWED.has(f.type) && !ALLOWED_EXT.includes(fileExt)) {
                    err = '이미지(JPG·PNG·GIF·WebP) 또는 PDF 파일만 첨부할 수 있어요.';
                    continue;
                }
                if (f.size > MAX_FILE) {
                    err = '파일 하나는 최대 5MB까지 첨부할 수 있어요.';
                    continue;
                }
                // 중복 제거
                if (next.some(x => x.name === f.name && x.size === f.size)) continue;
                // 총 용량 확인
                if (next.reduce((s, x) => s + x.size, 0) + f.size > MAX_TOTAL) {
                    err = '첨부 총 용량이 10MB를 초과해요.';
                    break;
                }
                next.push(f);
                nextTh.push(this.isImg(f) ? URL.createObjectURL(f) : null);
            }

            this.errorMsg = err;
            this.files    = next;
            this.thumbs   = nextTh;
            this.$emit('input', [...this.files]);
        },

        // ── 개별 삭제 ─────────────────────────────────────
        remove(i) {
            if (this.thumbs[i]) URL.revokeObjectURL(this.thumbs[i]);
            this.files  = this.files.filter((_, j) => j !== i);
            this.thumbs = this.thumbs.filter((_, j) => j !== i);
            this.errorMsg = '';
            this.$emit('input', [...this.files]);
        },

        // ── 내부 초기화 ───────────────────────────────────
        _reset() {
            this.thumbs.forEach(u => u && URL.revokeObjectURL(u));
            this.files   = [];
            this.thumbs  = [];
            this.errorMsg = '';
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },

        /** 외부에서 호출 가능한 초기화 (드로어 닫기 등) */
        reset() {
            this._reset();
            this.$emit('input', []);
        },

        // ── 헬퍼 ─────────────────────────────────────────
        isImg(f) { return f.type.startsWith('image/'); },
        isPDF(f) { return f.type === 'application/pdf'; },
        ext(f)   { return (f.name.split('.').pop() ?? '').toUpperCase().slice(0, 4); },
        fmtSize(b) {
            if (b < 1024)        return `${b} B`;
            if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
            return `${(b / (1024 * 1024)).toFixed(1)} MB`;
        },
    },
};
</script>

<style lang="scss" scoped>
@import './inquiry-tokens';

// ─── 로컬 별칭 ──────────────────────────────────────────────
$orange: $inq-orange;
$border: $inq-border-input;

/* ── 드롭존 ─────────────────────────────────────────────── */
.cfu__zone {
    display:        flex;
    flex-direction: column;
    align-items:    center;
    gap:            8px;
    width:          100%;
    padding:        24px 16px;
    border:         1.5px dashed $border;
    border-radius:  10px;
    background:     #fff;
    cursor:         pointer;
    text-align:     center;
    transition:     border-color .18s ease, background .18s ease;

    &:hover,
    &--over {
        border-color: $inq-hover-bdr;
        background:   $inq-hover-bg;
    }
    &--err {
        border-color: $inq-err-bdr;
        background:   $inq-err-bg;
    }
}

/* file input 은 시각적으로 숨김 */
.cfu__input {
    position:       absolute;
    width:          0;
    height:         0;
    opacity:        0;
    pointer-events: none;
}

/* ── 아이콘 / 버튼 레이블 / 안내 텍스트 ─────────────────── */
.cfu__icon { color: #9baab8; }

.cfu__btn-label {
    display:         inline-flex;
    align-items:     center;
    justify-content: center;
    height:          36px;
    padding:         0 16px;
    border-radius:   7px;
    background:      $orange;
    color:           #fff;
    font-size:       1.35rem;
    font-weight:     700;
    pointer-events:  none;
}

.cfu__guide {
    color:          $inq-text-sub;
    font-size:      1.2rem;
    line-height:    1.6;
    pointer-events: none;
}

/* ── 오류 메시지 ─────────────────────────────────────────── */
.cfu__errmsg {
    display:     block;
    margin-top:  8px;
    color:       $inq-err-clr;
    font-size:   1.2rem;
    font-weight: 600;
}

/* ── 파일 목록 ───────────────────────────────────────────── */
.cfu__list {
    list-style:     none;
    margin:         8px 0 0;
    padding:        0;
    display:        flex;
    flex-direction: column;
    gap:            8px;
}

.cfu__item {
    display:     flex;
    align-items: center;
    gap:         12px;
    padding:     8px 12px;
    border:      1px solid $inq-border-divider;
    border-radius: 8px;
    background:  #fff;
}

/* ── 썸네일 ─────────────────────────────────────────────── */
.cfu__thumb {
    flex-shrink:     0;
    width:           40px;
    height:          40px;
    border-radius:   6px;
    overflow:        hidden;
    background:      $inq-ctrl-bg;
    display:         flex;
    align-items:     center;
    justify-content: center;

    &--pdf { color: #c0392b; }
}

.cfu__thumb-img {
    width:      100%;
    height:     100%;
    object-fit: cover;
    display:    block;
}

.cfu__thumb-ext {
    font-size:   1.1rem;
    font-weight: 800;
    color:       #6b7280;
}

/* ── 파일 메타 ───────────────────────────────────────────── */
.cfu__meta {
    flex:           1 1 auto;
    min-width:      0;
    display:        flex;
    flex-direction: column;
    gap:            2px;
}

.cfu__name {
    font-size:     1.35rem;
    font-weight:   600;
    color:         $inq-text-primary;
    white-space:   nowrap;
    overflow:      hidden;
    text-overflow: ellipsis;
}

.cfu__size { font-size: 1.2rem; color: $inq-text-sub; }

/* ── 삭제 버튼 ───────────────────────────────────────────── */
.cfu__del {
    flex-shrink:     0;
    width:           28px;
    height:          28px;
    border:          none;
    border-radius:   50%;
    background:      $inq-ctrl-bg;
    color:           #6b7280;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    transition:      background .15s ease, color .15s ease;

    &:hover { background: $inq-del-hv-bg; color: $inq-del-hv-clr; }
}
</style>
